// Modal "Estructura del pedido" (Fase 10 / 7.2).
//
// Árbol jerárquico Palet → Cajas → Líneas. Fetch a /pedidos/{id}/estructura,
// re-fetch tras cualquier mutación (anular caja, editar caja, etc.).
//
// El modal no devuelve nada en su Promise — sirve para revisar y disparar
// acciones; la promesa se resuelve cuando el usuario cierra el modal. Las
// mutaciones notifican al caller vía callback `onChange()` opcional, para
// que la vista padre (empaquetado) pueda refrescar su estado si quiere.

import { apiDel, apiGet, apiPost } from '../api/client.js';
import { icon } from './icons.js';
import { openAlert } from './modal.js';
import { openEditarCajaModal } from './modal-editar-caja.js';
import { openMoverCajaModal } from './modal-mover-caja.js';

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g,
  c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));

function _last8(sscc) { return sscc.slice(-8); }

function _fmtFecha(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function _lineaHTML(linea) {
  const cant = Number.isInteger(linea.cantidad) ? linea.cantidad : linea.cantidad;
  const lote = linea.lote ? ` · lote ${escapeHtml(linea.lote)}` : '';
  return `<li style="display:flex;justify-content:space-between;gap:0.5rem;padding:0.25rem 0;border-bottom:1px dashed var(--c-border);font-size:var(--fs-sm);">
    <span>${escapeHtml(linea.descripcion)}<span style="color:var(--c-fg-muted);font-size:var(--fs-xs);">${lote}</span></span>
    <span style="font-weight:600;">×${cant}</span>
  </li>`;
}

function _cajaHTML(caja, indent = 1, paletActualSscc = null) {
  const last8 = _last8(caja.sscc);
  const fecha = _fmtFecha(caja.created_at);
  const peso = caja.peso_total_kg != null
    ? `<span style="color:var(--c-fg-muted);font-size:var(--fs-xs);"> · ${caja.peso_total_kg.toFixed(2)} kg</span>`
    : '';
  // Codificamos el palet actual como atributo "" (sin palet) o el SSCC del palet padre.
  const paletAttr = paletActualSscc === null ? '' : `data-palet="${escapeHtml(paletActualSscc)}"`;
  return `
    <details data-caja-sscc="${escapeHtml(caja.sscc)}" ${paletAttr} style="margin-left:${indent}rem;border:1px solid var(--c-border);border-radius:var(--radius);background:var(--c-surface);box-shadow:var(--shadow-sm);">
      <summary style="cursor:pointer;padding:0.55rem 0.7rem;display:flex;align-items:center;gap:0.5rem;list-style:none;">
        <span style="display:inline-flex;color:var(--c-fg-muted);">${icon('package', { size: 18 })}</span>
        <span style="flex:1;min-width:0;">
          <strong>Caja</strong>
          <span style="color:var(--c-fg-muted);font-family:ui-monospace,monospace;font-size:var(--fs-xs);"> · …${last8}</span>
          ${fecha ? `<span style="color:var(--c-fg-muted);font-size:var(--fs-xs);"> · ${fecha}</span>` : ''}
          <span style="color:var(--c-fg-muted);font-size:var(--fs-xs);"> · ${caja.lineas.length} ${caja.lineas.length === 1 ? 'producto' : 'productos'}${peso}</span>
        </span>
        <span style="color:var(--c-fg-soft);font-size:var(--fs-xs);">▾</span>
      </summary>
      <div style="padding:0 0.7rem 0.6rem;">
        <ul style="list-style:none;margin:0;padding:0;">${caja.lineas.map(_lineaHTML).join('')}</ul>
        <div style="display:flex;gap:0.4rem;margin-top:0.5rem;flex-wrap:wrap;">
          <button class="btn --ghost" data-action="mover" data-sscc="${escapeHtml(caja.sscc)}"
                  style="flex:1 1 30%;min-height:40px;font-size:var(--fs-sm);display:inline-flex;align-items:center;justify-content:center;gap:0.3rem;">${icon('layers', { size: 16 })} Mover</button>
          <button class="btn --ghost" data-action="anular" data-sscc="${escapeHtml(caja.sscc)}"
                  style="flex:1 1 30%;min-height:40px;font-size:var(--fs-sm);display:inline-flex;align-items:center;justify-content:center;gap:0.3rem;">${icon('trash-2', { size: 16 })} Anular</button>
          <button class="btn --primary" data-action="editar" data-sscc="${escapeHtml(caja.sscc)}"
                  style="flex:1 1 30%;min-height:40px;font-size:var(--fs-sm);display:inline-flex;align-items:center;justify-content:center;gap:0.3rem;">${icon('pencil', { size: 16 })} Editar</button>
        </div>
      </div>
    </details>`;
}

function _envioHTML(env) {
  const fecha = _fmtFecha(env.cerrado_at);
  const tipo = env.es_parcial ? 'Parcial' : 'Definitivo';
  const color = env.es_parcial ? 'var(--c-partial)' : 'var(--c-done)';
  return `
    <div style="
      background: var(--c-surface);
      border: 1px solid var(--c-border);
      border-left: 5px solid ${color};
      border-radius: var(--radius);
      padding: 0.6rem 0.85rem;
      display: flex; align-items: center; gap: 0.55rem;
    ">
      <span style="display:inline-flex;color:${color};">${icon('check-circle', { size: 22 })}</span>
      <div style="flex:1;min-width:0;">
        <div style="font-weight:600;">Envío #${env.numero_envio} · ${tipo}</div>
        <div style="color:var(--c-fg-muted);font-size:var(--fs-xs);">
          ${env.n_cajas} ${env.n_cajas === 1 ? 'caja' : 'cajas'}
          ${env.expedicion_id != null ? ` · DOL-${env.expedicion_id}` : ' · sin sincronizar'}
          ${fecha ? ` · ${fecha}` : ''}
        </div>
      </div>
    </div>`;
}

function _paletHTML(palet) {
  const last8 = _last8(palet.sscc);
  const totalCajas = palet.cajas.length;
  return `
    <details open style="margin-bottom:0.5rem;border:1px solid var(--c-border);border-left:5px solid var(--c-accent);border-radius:var(--radius-lg);background:var(--c-surface);box-shadow:var(--shadow-sm);">
      <summary style="cursor:pointer;padding:0.75rem 0.9rem;display:flex;align-items:center;gap:0.5rem;list-style:none;">
        <span style="display:inline-flex;color:var(--c-accent);">${icon('layers', { size: 24 })}</span>
        <span style="flex:1;min-width:0;">
          <strong>Palet #${palet.numero}</strong>
          <span style="color:var(--c-fg-muted);font-family:ui-monospace,monospace;font-size:var(--fs-xs);"> · …${last8}</span>
          <span style="color:var(--c-fg-muted);font-size:var(--fs-sm);display:block;">
            ${totalCajas} ${totalCajas === 1 ? 'caja' : 'cajas'}
          </span>
        </span>
        <span style="color:var(--c-fg-soft);">▾</span>
      </summary>
      <div style="padding:0.4rem 0.7rem 0.7rem;display:flex;flex-direction:column;gap:0.4rem;">
        ${totalCajas === 0
          ? `<div style="color:var(--c-fg-muted);font-size:var(--fs-sm);text-align:center;padding:0.4rem;">Palet vacío</div>
              <button class="btn --ghost" data-action="eliminar-palet" data-palet-sscc="${escapeHtml(palet.sscc)}"
                      style="min-height:40px;font-size:var(--fs-sm);display:inline-flex;align-items:center;justify-content:center;gap:0.3rem;color:var(--c-error);">
                ${icon('trash-2', { size: 16 })} Eliminar palet
              </button>`
          : palet.cajas.map(c => _cajaHTML(c, 0, palet.sscc)).join('')}
      </div>
    </details>`;
}

let _activeHost = null;

function _close() {
  if (_activeHost) {
    _activeHost.remove();
    _activeHost = null;
  }
}

export function openEstructuraModal({ pedidoId, onChange }) {
  return new Promise((resolve) => {
    _close();  // si ya hay uno, lo reemplazamos
    _activeHost = document.createElement('div');
    document.body.appendChild(_activeHost);

    let estructura = null;
    let mounted = true;

    async function refetch() {
      try {
        estructura = await apiGet(`/pedidos/${pedidoId}/estructura`);
      } catch (e) {
        if (e?.status === 401) { close(); return; }
        await openAlert({
          tipo: 'error',
          titulo: 'No se pudo cargar la estructura',
          detalle: e?.message || 'Error desconocido',
        });
        return;
      }
      if (mounted) render();
    }

    function close() {
      mounted = false;
      _close();
      resolve();
    }

    function render() {
      if (!_activeHost) return;
      const palets = estructura?.palets || [];
      const sueltas = estructura?.cajas_sin_palet || [];

      const totalCajasPalets = palets.reduce((sum, p) => sum + p.cajas.length, 0);
      const totalCajas = totalCajasPalets + sueltas.length;
      const subtitulo = totalCajas === 0
        ? 'Sin cajas todavía'
        : `${palets.length} ${palets.length === 1 ? 'palet' : 'palets'} · ${totalCajas} ${totalCajas === 1 ? 'caja' : 'cajas'}`;

      _activeHost.innerHTML = `
        <div class="scrim fade-in" style="align-items:stretch;">
          <div class="modal" style="
              border-radius: 0;
              max-width: 100%;
              max-height: 100dvh;
              height: 100dvh;
              padding: 0;
              display: flex;
              flex-direction: column;
              gap: 0;
              overflow: hidden;
            ">
            <header style="
                padding: 0.75rem 1rem;
                background: var(--c-primary);
                color: var(--c-primary-fg);
                display: flex;
                align-items: center;
                gap: 0.5rem;
                position: sticky; top: 0;
              ">
              <span style="display:inline-flex;">${icon('folder-tree', { size: 22 })}</span>
              <h2 style="margin:0;flex:1;font-size:var(--fs-lg);color:#fff;display:block;">
                Estructura
                <span style="display:block;font-size:var(--fs-xs);color:rgba(255,255,255,0.7);font-weight:400;">${escapeHtml(subtitulo)}</span>
              </h2>
              <button class="iconbtn" id="estr-close" title="Cerrar" aria-label="Cerrar">${icon('x', { size: 22 })}</button>
            </header>
            <div style="flex:1;overflow-y:auto;padding:1rem;display:flex;flex-direction:column;gap:0.5rem;background:var(--c-bg);">
              ${(estructura?.envios_realizados || []).length > 0 ? `
                <h3 class="section-title">Envíos realizados</h3>
                ${estructura.envios_realizados.map(_envioHTML).join('')}
                <h3 class="section-title" style="margin-top:0.5rem;">En curso (no enviadas)</h3>
              ` : ''}
              ${palets.map(_paletHTML).join('')}
              ${sueltas.length > 0 ? `
                <h3 class="section-title">Cajas sin palet</h3>
                ${sueltas.map(c => _cajaHTML(c, 0, null)).join('')}
              ` : ''}
              ${totalCajas === 0 ? `
                <div class="empty-state" style="margin:auto;">
                  <div class="icon" style="display:flex;justify-content:center;">${icon('package', { size: 48 })}</div>
                  <p style="margin:0;">${(estructura?.envios_realizados || []).length > 0 ? 'No hay cajas en curso. Crea una caja nueva para hacer otro envío.' : 'El pedido no tiene cajas todavía.'}</p>
                </div>` : ''}
            </div>
          </div>
        </div>`;

      _activeHost.querySelector('#estr-close').addEventListener('click', close);

      // Delegación de acciones en las cajas (anular / editar).
      _activeHost.querySelectorAll('button[data-action]').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          const action = btn.dataset.action;
          const sscc = btn.dataset.sscc;
          if (action === 'anular') {
            const confirm = await openAlert({
              tipo: 'warn',
              titulo: '¿Anular esta caja?',
              detalle: `Quedará marcada como anulada y desaparecerá de la lista. SSCC ${sscc}.`,
              accion: 'Anular',
              accion2: 'Cancelar',
            });
            if (confirm !== 'primary') return;
            try {
              await apiDel(`/cajas/${sscc}`);
            } catch (e2) {
              if (e2?.status === 401) return;
              await openAlert({ tipo: 'error', titulo: 'No se pudo anular', detalle: e2?.message || 'Error' });
              return;
            }
            await refetch();
            onChange?.();
          } else if (action === 'editar') {
            // Buscar la caja en la estructura para pasar sus líneas iniciales al modal.
            const caja = _findCaja(estructura, sscc);
            if (!caja) return;
            const result = await openEditarCajaModal({ caja });
            if (result?.reemitida) {
              await refetch();
              onChange?.();
            }
          } else if (action === 'eliminar-palet') {
            const paletSscc = btn.dataset.paletSscc;
            const confirm = await openAlert({
              tipo: 'warn',
              titulo: '¿Eliminar palet vacío?',
              detalle: `SSCC ${paletSscc} desaparecerá de la estructura.`,
              accion: 'Eliminar',
              accion2: 'Cancelar',
            });
            if (confirm !== 'primary') return;
            try {
              await apiDel(`/palets/${paletSscc}`);
            } catch (e2) {
              if (e2?.status === 401) return;
              if (e2?.status === 409) {
                await openAlert({ tipo: 'warn', titulo: 'No se puede eliminar', detalle: e2?.message || 'Palet con cajas' });
                return;
              }
              await openAlert({ tipo: 'error', titulo: 'No se pudo eliminar', detalle: e2?.message || 'Error' });
              return;
            }
            await refetch();
            onChange?.();
          } else if (action === 'mover') {
            const caja = _findCaja(estructura, sscc);
            if (!caja) return;
            // Palet actual: lo sacamos del data-palet del <details> contenedor
            // (string vacío "" = sin palet → null).
            const detailsEl = btn.closest('details[data-caja-sscc]');
            const dp = detailsEl?.dataset.palet;
            const currentPaletSscc = (dp && dp.length) ? dp : null;
            const target = await openMoverCajaModal({
              caja,
              palets: estructura?.palets || [],
              currentPaletSscc,
            });
            if (target === undefined) return;  // cancelado
            try {
              await apiPost(`/cajas/${sscc}/mover`, { palet_sscc: target });
            } catch (e3) {
              if (e3?.status === 401) return;
              await openAlert({ tipo: 'error', titulo: 'No se pudo mover', detalle: e3?.message || 'Error' });
              return;
            }
            await refetch();
            onChange?.();
          }
        });
      });
    }

    refetch();
  });
}

function _findCaja(estructura, sscc) {
  for (const p of estructura?.palets || []) {
    for (const c of p.cajas) if (c.sscc === sscc) return c;
  }
  for (const c of estructura?.cajas_sin_palet || []) {
    if (c.sscc === sscc) return c;
  }
  return null;
}
